import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { FETCH_ERROR, FETCH_START, FETCH_SUCCESS } from '../../constants/ActionTypes';
import customAxios, { customFormAxios } from '../../util/Api';
import authHeader from '../../../../services/auth/jwt/auth-header';
import { useToast } from 'context/tostifyContext';
import { useHistory, useLocation } from 'react-router-dom';
import { toLocalISO } from 'routes/Pages/GenTask/components/SharedFormFields';

// ─── Constants ────────────────────────────────────────────────────────────────

const INITIAL_DOCKET_INFO_STATE = {
  loadingDetails: [],
  journeyDetails: [],
  transfers: [],
  weighbridge: [],
};

const NOSECTION_SELECT = { sectionName: '', sectionData: '' };

// The 5 filterable columns the UI exposes.
// apiColumn   = sent as `filterColumn` to GetFilteredDockets
// distinctKey = sent as `columnName`   to GetDistinctFilterValues
export const FILTER_DEFINITIONS = [
  { key: 'driverUserName', label: 'Entered By', apiColumn: 'driverUserName', distinctKey: 'driverUserName' },
  { key: 'driverName', label: 'Driver', apiColumn: 'driverName', distinctKey: 'driverName' },
  { key: 'submissionDate', label: 'Sub Date', apiColumn: 'submissionDate', distinctKey: 'submissionDate' },
  { key: 'forestSP', label: 'SP', apiColumn: 'forestSP', distinctKey: 'forestSP' },
  {
    key: 'timberProductUsedForWoodchip',
    label: 'Product',
    apiColumn: 'timberProductUsedForWoodchip',
    distinctKey: 'timberProductUsedForWoodchip',
  },
];

// Only ONE filterColumn+filterValue is active at a time (API constraint).
export const INITIAL_FILTER = {
  filterColumn: '', // apiColumn value or ''
  filterValue: '', // chosen value
  searchDocketNumber: '', // maps to `docketNumber` param
  searchUsername: '', // maps to `username` param
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

const useDocket = () => {
  const { showToast } = useToast();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const isMounted = useRef(true);
  const gridRef = useRef(null);

  const [docketData, setDocketData] = useState([]);
  const [totalDocketCount, setTotalDocketCount] = useState(0);
  const [openDocketData, setOpenDocketData] = useState([]);
  const [selectedDocket, setSelectedDocket] = useState({});
  const [tabValue, setTabValue] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);

  // Active filter state
  const [activeFilter, setActiveFilter] = useState(INITIAL_FILTER);

  // Distinct values cache: { [key]: string[] }
  const [distinctValues, setDistinctValues] = useState({});
  const [distinctLoading, setDistinctLoading] = useState({});

  const [docketInfoData, setDocketInfoData] = useState(INITIAL_DOCKET_INFO_STATE);
  const [openDocketInfoModal, setOpenDocketInfoModal] = useState(false);
  const [selectedDocketSection, setSelectedDocketSection] = useState(NOSECTION_SELECT);
  const [openDocketEditModal, setOpenDocketEditModal] = useState(false);

  const [files, setFiles] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [docType, setDocType] = useState('');

  const [isDocketDataLoading, setIsDocketDataLoading] = useState(false);
  const [allUsers, setAllUsers] = useState({});

  // ── Startup ──────────────────────────────────────────────────────────────

  useEffect(() => {
    isMounted.current = true;

    const sp = new URLSearchParams(location.search);
    const docketNumber = sp.get('docket');

    if (docketNumber && !openDocketInfoModal) {
      (async () => {
        const rows = await getDocketByNumber(docketNumber);
        if (rows?.length) {
          await getDocketInfo(rows[0]);
          setSelectedDocket(rows[0]);
          setOpenDocketInfoModal(true);
        }
      })();
    } else if (!docketNumber) {
      // ✅ FIXED: Preserve the active filter when closing the docket dialog
      // Only reset to INITIAL_FILTER if no filter was previously set
      const filterToUse =
        activeFilter.filterColumn || activeFilter.searchDocketNumber || activeFilter.searchUsername
          ? activeFilter
          : INITIAL_FILTER;
      fetchFilteredDockets(1, pageSize, filterToUse);
      fetchOpenDockets();
      fetchTotalCount();
    }

    (async () => {
      try {
        const { data } = await customAxios.get('/User/GetAllUsers', { headers: authHeader() });
        const map = Object.fromEntries(data.map(u => [u.email.toLowerCase(), u.displayName]));
        if (isMounted.current) setAllUsers(map);
      } catch (err) {
        console.error('getUsers error', err);
      }
    })();

    return () => {
      isMounted.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // ── Distinct filter values (lazy, cached) ─────────────────────────────────

  const fetchDistinctValues = async key => {
    if (distinctValues[key] !== undefined || distinctLoading[key]) return;
    const def = FILTER_DEFINITIONS.find(f => f.key === key);
    if (!def) return;

    setDistinctLoading(prev => ({ ...prev, [key]: true }));
    try {
      const { data } = await customAxios.get(
        `/Ops/GetDistinctFilterValues?columnName=${encodeURIComponent(def.distinctKey)}`,
        { headers: authHeader() },
      );
      const values = Array.isArray(data) ? data.map(d => d.filterValue).filter(Boolean) : [];
      if (isMounted.current) setDistinctValues(prev => ({ ...prev, [key]: values }));
    } catch (err) {
      console.error(`fetchDistinctValues(${key}) error`, err);
    } finally {
      if (isMounted.current) setDistinctLoading(prev => ({ ...prev, [key]: false }));
    }
  };

  // ── Total count ───────────────────────────────────────────────────────────

  const fetchTotalCount = async () => {
    try {
      const { data } = await customAxios.get('/Ops/GetTotalDockets', { headers: authHeader() });
      if (isMounted.current) setTotalDocketCount(data?.totalDockets ?? 0);
    } catch (err) {
      console.error('fetchTotalCount error', err);
    }
  };

  // ── Core fetch: GetFilteredDockets ────────────────────────────────────────

  const fetchFilteredDockets = async (page = 1, size = pageSize, filter = activeFilter) => {
    setIsDocketDataLoading(true);
    dispatch({ type: FETCH_START });

    try {
      const params = new URLSearchParams();
      params.append('rowOffset', String((page - 1) * size));
      params.append('fetchNextRows', String(size));

      if (filter.searchDocketNumber) params.append('docketNumber', filter.searchDocketNumber);
      if (filter.searchUsername) params.append('username', filter.searchUsername);
      if (filter.filterColumn && filter.filterValue) {
        params.append('filterColumn', filter.filterColumn);
        params.append('filterValue', filter.filterValue);
      }

      const { data } = await customAxios.get(`/Ops/GetFilteredDockets?${params.toString()}`, { headers: authHeader() });
      const rows = Array.isArray(data) ? data : [];

      if (isMounted.current) {
        setDocketData(rows);
        setCurrentPage(page);
        setPageSize(size);

        // When a filter is active the true total may differ;
        // use a heuristic until the API returns a total field.
        const isFiltered = filter.filterColumn || filter.searchDocketNumber || filter.searchUsername;
        if (isFiltered) {
          const est = rows.length < size ? (page - 1) * size + rows.length : page * size + 1;
          setTotalDocketCount(est);
        }

        dispatch({ type: FETCH_SUCCESS });
      }
    } catch (error) {
      if (isMounted.current) {
        setDocketData([]);
        dispatch({ type: FETCH_ERROR, payload: 'Network Error' });
        console.error('fetchFilteredDockets error', error);
      }
    } finally {
      if (isMounted.current) setIsDocketDataLoading(false);
    }
  };

  // ── Apply / clear filter ──────────────────────────────────────────────────

  const handleApplyFilter = newFilter => {
    setActiveFilter(newFilter);
    setCurrentPage(1);
    const isUnfiltered = !newFilter.filterColumn && !newFilter.searchDocketNumber && !newFilter.searchUsername;
    if (isUnfiltered) fetchTotalCount();
    fetchFilteredDockets(1, pageSize, newFilter);
  };

  const handleClearFilter = () => {
    setActiveFilter(INITIAL_FILTER);
    setCurrentPage(1);
    fetchTotalCount();
    fetchFilteredDockets(1, pageSize, INITIAL_FILTER);
  };

  // ── Open dockets ──────────────────────────────────────────────────────────

  const fetchOpenDockets = async () => {
    try {
      const { data } = await customAxios.get('Ops/GetOpenDockets', { headers: authHeader() });
      const rows = Array.isArray(data) ? data : [];
      if (isMounted.current)
        console.log("Fetched open dockets:", rows, "rows");
        setOpenDocketData(rows.filter(r => r.isDocketOpen !== 'CLOSED' && r.isDocketOpen !== 'CANCELLED'));
    } catch (err) {
      console.error('fetchOpenDockets error', err);
    }
  };

  // ── Deep-link lookup ──────────────────────────────────────────────────────

  const getDocketByNumber = async docketNumber => {
    try {
      const { data } = await customAxios.get(`/Ops/GetDockets/${docketNumber}`, { headers: authHeader() });
      return Array.isArray(data) ? data : [data];
    } catch (err) {
      console.error('getDocketByNumber error', err);
      return [];
    }
  };

  // ── Page change ───────────────────────────────────────────────────────────

  const handleGridPageChange = ({ currentPage: newPage, pageSize: newSize }) => {
    const size = newSize ?? pageSize;
    setPageSize(size);
    fetchFilteredDockets(newPage ?? 1, size, activeFilter);
  };

  // ── Tab change ────────────────────────────────────────────────────────────

  const handleTabChange = (_e, newVal) => {
    setTabValue(newVal);
    if (newVal === 0) {
      fetchOpenDockets();
    } else {
      setActiveFilter(INITIAL_FILTER);
      setCurrentPage(1);
      fetchTotalCount();
      fetchFilteredDockets(1, pageSize, INITIAL_FILTER);
    }
  };

  // ── Docket info ───────────────────────────────────────────────────────────

  const getDocketInfo = async docketRow => {
    try {
      setSelectedDocket(docketRow);
      const { status, data: apiData } = await customAxios.get(`/Ops/GetDocketOptions/${docketRow.docketNumber}`, {
        headers: authHeader(),
      });
      if (status === 200) {
        fetchDocketDocs(docketRow.docketNumber);
        setDocketInfoData({
          loadingDetails: [
            {
              driverName: docketRow.driverName || '',
              dateAndTime: docketRow.dateAndTime ? toLocalISO(docketRow.dateAndTime) : toLocalISO(),
              loadedBy: docketRow.loaderDriver || '',
              loaderMachineOrTruck: docketRow.loaderMachineOrTruck || '',
              driverUserName: docketRow.driverUserName || '',
              docketId: docketRow.id || '',
              spNumber: docketRow.forestSP || '',
              truck: docketRow.truck || '',
              trailer: docketRow.trailer || '',
              productType: docketRow.productType || '',
              timberProductUsedForWoodchip: docketRow.timberProductUsedForWoodchip || '',
              whDocketNumber: docketRow.wH_DocketNumber || '',
              name: docketRow.name || docketRow.customerForOtherWork || '',
              supplierWeighbridgeGrWgt: docketRow.supplierWeighbridgeGrWgt || 0,
              supplierWeighbridgeNetWgt: docketRow.supplierWeighbridgeNetWgt || 0,
              docketNumber: docketRow.docketNumber || 'NA',
              permitNumber: docketRow.permitNumber || '',
              supplierDocketNumber: docketRow.supplierDocketNumber || '',
              internalNoteforWH: docketRow.internalNoteforWH || '',
            },
          ],
          journeyDetails: apiData?.journeyDetails || [],
          transfers: apiData?.transfers || [],
          weighbridge: apiData?.weighbridge || [],
        });
      } else {
        setDocketInfoData(INITIAL_DOCKET_INFO_STATE);
      }
    } catch (err) {
      console.error('getDocketInfo error', err);
      setDocketInfoData(INITIAL_DOCKET_INFO_STATE);
    }
  };

  // ── Modal open / close ────────────────────────────────────────────────────

  const handleOpenDocketInfoModal = async docketRow => {
    getDocketInfo(docketRow);
    setOpenDocketInfoModal(true);
    const sp = new URLSearchParams(location.search);
    sp.set('docket', docketRow.docketNumber);
    history.replace({ pathname: location.pathname, search: sp.toString() });
  };

  const handleCloseDocketInfoModal = () => {
    setSelectedDocket({});
    setOpenDocketInfoModal(false);
    const sp = new URLSearchParams(location.search);
    sp.delete('docket');
    history.replace({ pathname: location.pathname, search: sp.toString() });
  };

  // ── Edit / save docket section ────────────────────────────────────────────

  const handleDocketSectionEdit = async (sectionName, editedData) => {
    let url = '';
    if (sectionName === 'journeyDetails') url = '/Ops/EditDocketJourneyDetails';
    else if (sectionName === 'transfers') url = '/Ops/EditDocketsTransfers';
    else if (sectionName === 'weighbridge' && selectedDocketSection.mode === 'EDIT') url = '/Ops/EditDocketsWeighbridge';
    else if (sectionName === 'weighbridge' && selectedDocketSection.mode === 'ADD') url = '/Ops/AddDocketsWeighbridge';

    if (sectionName === 'loadingDetails') {
      const destinationChanged =
        editedData?.name && editedData.name !== selectedDocket?.name;

      let filteredDestinations = null;

      try {
        const response = await customAxios.get(`Ops/GetContractProductDestinations/${editedData?.spNumber}`, {
          headers: authHeader(),
        });

        const destinations = Array.isArray(response?.data) ? response.data : [];

        filteredDestinations = destinations.find(item => item.destination === editedData?.name);
      } catch (error) {
        console.error('Failed to fetch contract destinations:', error);
        if (destinationChanged) {
          // Can't verify the new destination's site assessment — do not proceed
          // with a stale/incorrect ID.
          alert('Could not verify the site assessment for the new destination. Edit was not saved.');
          return;
        }
      }

      // If the destination changed, we MUST have a valid siteAssessmentId for it.
      // Never fall back to the old docket's ID in that case.
      if (destinationChanged && !filteredDestinations?.siteAssessmentId) {
        console.error(
          `Destination changed to "${editedData?.name}" but no matching site assessment was found for SP ${editedData?.spNumber}.`
        );
        alert('The selected destination has no associated site assessment. Edit was not saved.');
        return; // fail the whole edit — don't call the API
      }

      const resolvedSiteAssessmentID = destinationChanged
        ? filteredDestinations.siteAssessmentId
        : (filteredDestinations?.siteAssessmentId || selectedDocket?.destinationSiteAssessmentID);

      const payload = {
        ...selectedDocket,
        docketNumber: selectedDocket.docketNumber || '',
        driverName: editedData.driverName || selectedDocket.driverName || '',
        loaderDriver: editedData.loadedBy || selectedDocket.loaderDriver || '',
        productType: editedData.productType || selectedDocket.productType || '',
        timberProductUsedForWoodchip:
          editedData.timberProductUsedForWoodchip || selectedDocket.timberProductUsedForWoodchip || '',
        forestSP: editedData.spNumber || selectedDocket.forestSP || '',
        truck: editedData.truck || selectedDocket.truck || '',
        trailer: editedData.trailer || selectedDocket.trailer || '',
        wH_DocketNumber: editedData.whDocketNumber || selectedDocket.wH_DocketNumber || '',
        name: editedData.name || selectedDocket.name || '',
        driverUserName: editedData.driverUserName,
        permitNumber: editedData.permitNumber || '',
        supplierDocketNumber: editedData.supplierDocketNumber || '',
        supplierWeighbridgeGrWgt: editedData.supplierWeighbridgeGrWgt || 0,
        supplierWeighbridgeNetWgt: editedData.supplierWeighbridgeNetWgt || 0,
        internalNoteforWH: editedData.internalNoteforWH || '',
        isSplitLoad: typeof editedData?.isSplitLoad === 'number' ? editedData?.isSplitLoad : 0,
        destinationSiteAssessmentID: resolvedSiteAssessmentID,
        isLastLoad: editedData.isLastLoad ?? selectedDocket.isLastLoad ?? 0,
      };

      try {
        const res = await customAxios.post('/Ops/EditDocket', payload, { headers: authHeader() });
        if (res.status === 200) {
          await getDocketInfo(selectedDocket);
          setOpenDocketEditModal(false);
          setOpenDocketInfoModal(false);

          if (!selectedDocket?.isDocketOpen) {
            const wbPayload = {
              destinationSiteAssessmentID: resolvedSiteAssessmentID,
            };
            await customAxios.post('/Ops/GetCustomerWeighbridgeDetails', wbPayload, {
              headers: authHeader(),
            });
          }

          fetchFilteredDockets(currentPage, pageSize, activeFilter);
        }
      } catch (err) {
        console.error('Edit loadingDetails error', err);
      }
      return;
    }

    const weighbridgePayload = {
      submissionDate: editedData.submissionDate || selectedDocket?.submissionDate,
      docketNumber: selectedDocket.docketNumber || '',
      customer: editedData.customer || selectedDocket.customer,
      weightBridge: editedData.weightBridge || selectedDocket.weightBridge,
      unloadingDetailsGrWgt: editedData.unloadingDetailsGrWgt || selectedDocket.unloadingDetailsGrWgt,
      unloadingDetailsNetWgt: editedData.unloadingDetailsNetWgt || selectedDocket.unloadingDetailsNetWgt,
      wH_Docket: selectedDocket.wH_Docket,
      weightbridgeDocketNumber: editedData.weightbridgeDocketNumber || selectedDocket.weightbridgeDocketNumber,
      wB_Docket: editedData.wB_Docket || selectedDocket.wB_Docket,
      permitNumber: selectedDocket?.permitNumber || '',
      permit: selectedDocket?.permit,
      supplierDocketNumber: selectedDocket?.supplierDocketNumber,
      supplierDocket: selectedDocket?.supplierDocket,
      additionalInformation: editedData.additionalInformation || selectedDocket?.additionalInformation,
      recordModifiedDate: selectedDocket?.recordModifiedDate,
    };

    if (selectedDocketSection.mode === 'ADD') {
      const res = await customAxios.post(url, weighbridgePayload, { headers: authHeader() });
      if (res.status === 200) {
        getDocketInfo(selectedDocket);
        setOpenDocketEditModal(false);
        setOpenDocketInfoModal(false);
        fetchFilteredDockets(currentPage, pageSize, activeFilter);
      }
      return;
    }

    const res = await customAxios.post(url, editedData, { headers: authHeader() });
    if (res.status === 200) {
      getDocketInfo(selectedDocket);
      setOpenDocketEditModal(false);
    }
  };

  // ── Cancel load ───────────────────────────────────────────────────────────

  const normalizePayload = obj => Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== null && v !== ''));

  const cancelLoad = async rowData => {
    try {
      const res = await customAxios.post(
        '/Ops/EditDocket',
        normalizePayload({ ...rowData, isSplitLoad: 0, cancelLoad: 'Yes' }),
        {
          headers: authHeader(),
        },
      );
      if (res.status === 200) {
        showToast('Load cancelled successfully', 'success');
        fetchFilteredDockets(currentPage, pageSize, activeFilter);
        fetchOpenDockets();
      } else showToast('Failed to cancel the load', 'error');
    } catch (err) {
      console.error(err);
      showToast('Failed to cancel the load', 'error');
    }
  };

  // ── Edit section modal ────────────────────────────────────────────────────

  const handleDocketInfoEditStart = (sectionName, docketRow) => {
    setSelectedDocketSection({ sectionName, sectionData: docketRow ?? {}, mode: docketRow ? 'EDIT' : 'ADD' });
    setOpenDocketEditModal(true);
  };

  const handleDocketInfoEditClose = () => {
    setSelectedDocketSection({});
    setOpenDocketEditModal(false);
    const sp = new URLSearchParams(location.search);
    sp.delete('docket');
    history.replace({ pathname: location.pathname, search: sp.toString() });
  };

  // ── Export to CSV ─────────────────────────────────────────────────────────

  const handleExportToExcel = async () => {
    try {
      setIsDocketDataLoading(true);
      const params = new URLSearchParams();
      params.append('rowOffset', '0');
      params.append('fetchNextRows', '999999');
      if (activeFilter.searchDocketNumber) params.append('docketNumber', activeFilter.searchDocketNumber);
      if (activeFilter.searchUsername) params.append('username', activeFilter.searchUsername);
      if (activeFilter.filterColumn && activeFilter.filterValue) {
        params.append('filterColumn', activeFilter.filterColumn);
        params.append('filterValue', activeFilter.filterValue);
      }

      const { data } = await customAxios.get(`/Ops/GetFilteredDockets?${params.toString()}`, { headers: authHeader() });
      const rows = Array.isArray(data) ? data : [];
      if (!rows.length) {
        showToast('No data to export', 'warning');
        return;
      }

      const hdrs = [
        'Status',
        'Docket Number',
        'Entered By',
        'Driver',
        'Loaded By',
        'Submission Date',
        'Date & Time',
        'SP',
        'Truck',
        'Trailer',
        'Product Type',
        'Product',
        'WH Docket',
        'WB Docket',
        'Customer',
        'Destination',
        'Gross Weight',
        'Net Weight',
        'Cancel Load',
      ];
      const esc = v => {
        if (v == null) return '';
        const s = String(v);
        return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
      };
      const csv = [
        hdrs.join(','),
        ...rows.map(r =>
          [
            r.isDocketOpen === 'OPEN' ? 'Open' : r.cancelLoad === 'Yes' ? 'CANCELLED' : '',
            r.docketNumber,
            r.driverUserName,
            r.driverName,
            r.loaderDriver,
            r.submissionDate,
            r.dateAndTime,
            r.forestSP,
            r.truck,
            r.trailer,
            r.productType,
            r.timberProductUsedForWoodchip,
            r.wH_DocketNumber,
            r.weightbridgeDocketNumber,
            r.customerName,
            r.name,
            r.unloadingDetailsGrWgt,
            r.unloadingDetailsNetWgt,
            r.cancelLoad,
          ]
            .map(esc)
            .join(','),
        ),
      ].join('\n');

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `Dockets_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
      showToast('Export complete', 'success');
    } catch (err) {
      console.error('Export error', err);
      showToast('Export failed', 'error');
    } finally {
      if (isMounted.current) setIsDocketDataLoading(false);
    }
  };

  // ── Docs ──────────────────────────────────────────────────────────────────

  const fetchDocketDocs = async docketNumber => {
    const { data } = await customAxios.get(`/Ops/GetDocketsDocs/${docketNumber}`, { headers: authHeader() });
    setFiles(data);
  };

  const handleDocketDocs = rowData => {
    if (!rowData?.column) {
      setIsOpen(false);
      return;
    }
    const map = { wH_Docket: 'whDocs', wB_Docket: 'wbDocs', permit: 'permitDocs', supplierDocket: 'supplierDocs' };
    setDocType(map[rowData.column.field] ?? '');
    setIsOpen(prev => !prev);
  };

  const handleFileChange = _key => async file => {
    if (!docType) {
      showToast('Kindly select a document type!');
      return;
    }
    const formData = new FormData();
    formData.append('docketNumber', selectedDocket.docketNumber);
    formData.append('docType', docType);
    formData.append('files', file?.[0]);
    try {
      const res = await customFormAxios.post('/Ops/AddDocketsDocs', formData, { headers: authHeader() });
      if (res.status === 200) {
        showToast('File uploaded successfully', 'success');
        fetchDocketDocs(selectedDocket.docketNumber);
      }
    } catch (err) {
      console.error(err);
      showToast('Something went wrong', 'error');
    }
  };

  const handleDeleteFiles = fileData => {
    if (!window.confirm('Are you sure you want to delete this Document?')) return;
    dispatch({ type: FETCH_START });
    customAxios
      .post('/Ops/DeleteDocketsDocs', { id: fileData.id }, { headers: authHeader() })
      .then(res => {
        if (res.status === 200) {
          showToast('File deleted successfully.', 'success');
          fetchDocketDocs(selectedDocket.docketNumber);
        }
        dispatch({ type: FETCH_SUCCESS });
      })
      .catch(err => {
        console.error(err);
        dispatch({ type: FETCH_ERROR, payload: 'Network Error' });
      });
  };

  const viewFile = async fileData => {
    try {
      dispatch({ type: FETCH_START });
      const res = await customAxios.get(`/Ops/GetDocketsFile/${fileData.id}`, {
        headers: authHeader(),
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileData.documentName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      dispatch({ type: FETCH_SUCCESS });
    } catch (err) {
      console.error(err);
      dispatch({ type: FETCH_ERROR, payload: 'Network Error' });
    }
  };

  // ── Return ────────────────────────────────────────────────────────────────

  return {
    docketData,
    totalDocketCount,
    openDocketData,
    currentPage,
    pageSize,
    handleGridPageChange,
    tabValue,
    handleTabChange,
    activeFilter,
    handleApplyFilter,
    handleClearFilter,
    distinctValues,
    distinctLoading,
    fetchDistinctValues,
    docketInfoData,
    openDocketInfoModal,
    setOpenDocketInfoModal,
    selectedDocket,
    getDocketInfo,
    handleOpenDocketInfoModal,
    handleCloseDocketInfoModal,
    selectedDocketSection,
    openDocketEditModal,
    setOpenDocketEditModal,
    handleDocketInfoEditStart,
    handleDocketSectionEdit,
    files,
    isOpen,
    docType,
    gridRef,
    handleDocketDocs,
    handleFileChange,
    handleDeleteFiles,
    viewFile,
    isDocketDataLoading,
    allUsers,
    cancelLoad,
    handleExportToExcel,
    handleDocketInfoEditClose,
  };
};

export default useDocket;
